import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { COLORS, SPACING } from '../pdf/designSystem';
import { PdfSectionTitle, PdfHeader, PdfMetaRow, PdfTotalsBlock, PdfApprovalSection, PdfFooter, PdfItemsTable } from './designSystem/reactPdfHelpers';
import { computeDpTotals } from '../utils/dpFinancials';
import { formatCurrency as fmtCAD } from '../utils/formatCurrency';

// ─── Label Map ────────────────────────────────────────────────
const labels = {
  en: {
    title:          'PAYMENT REQUEST',
    titleSub:       'DEMANDE DE PAIEMENT',
    dpLabel:        'N° / N°',
    dateLabel:      'Date',
    statusLabel:    'Status · Statut',
    contractualBanner: 'TRAVAUX CONTRACTUEL · CONTRACT WORK',
    extrasBanner:      'TRAVAUX EXTRAS · EXTRA WORK',
    billTo:         'BILL TO · FACTURÉ À',
    project:        'PROJECT · PROJET',
    columns: {
      description:    'DESCRIPTION · WORK DESC',
      contractValue:  'CONTRACT VALUE ($)',
      toDateParent:   'TO DATE · À CE JOUR',
      lastReqParent:  'LAST REQUEST · DERNIÈRE DEMANDE',
      thisRequest:    'THIS REQUEST ($)',
      pct:            '(%)',
      dollar:         '($)',
    },
    infoLabels: {
      name:           'Name · Nom',
      company:        'Company · Entreprise',
      address:        'Address · Adresse',
      phone:          'Phone · Téléphone',
      email:          'Email · Courriel',
      projectName:    'Project · Projet',
      projectNumber:  'No. · N° projet',
      site:           'Site · Adresse',
      pm:             'PM · Chargé',
      period:         'Payment Request Period · Période de la demande de paiement',
    },
    totalsRows: {
      subtotal:              'Subtotal · Sous-total',
      holdback:              'Holdback (10%) · Retenue',
      subtotalAfterHoldback: 'After holdback · Sous-total après retenue',
      tps:                   'TPS (5%)',
      tvq:                   'TVQ (9.975%)',
      netTotal:              'TOTAL NET · NET TOTAL',
    },
    termsLine1: '· Payment request based on work completed as of {period_end}.',
    termsLine2: '· Demande conforme aux travaux exécutés au {period_end}.',
    termsLine3: '· Submitted per contract terms. · Soumis selon les modalités du contrat.',
    noClient: '—',
    page: (n, t) => `Page ${n} of ${t}`,
  },
  fr: {
    title:          'DEMANDE DE PAIEMENT',
    titleSub:       'PAYMENT REQUEST',
    dpLabel:        'N° / N°',
    dateLabel:      'Date',
    statusLabel:    'Statut · Status',
    contractualBanner: 'TRAVAUX CONTRACTUEL · CONTRACT WORK',
    extrasBanner:      'TRAVAUX EXTRAS · EXTRA WORK',
    billTo:         'FACTURÉ À · BILL TO',
    project:        'PROJET · PROJECT',
    columns: {
      description:    'DESCRIPTION DÉTAILS · WORK DESC',
      contractValue:  'VALEUR À EXÉCUTER ($)',
      toDateParent:   'À CE JOUR · TO DATE',
      lastReqParent:  'DERNIÈRE DEMANDE · LAST REQUEST',
      thisRequest:    'PRÉSENTE DEMANDE ($)',
      pct:            '(%)',
      dollar:         '($)',
    },
    infoLabels: {
      name:           'Nom · Name',
      company:        'Entreprise · Company',
      address:        'Adresse · Address',
      phone:          'Téléphone · Phone',
      email:          'Courriel · Email',
      projectName:    'Projet · Project',
      projectNumber:  'N° projet · No.',
      site:           'Adresse · Site',
      pm:             'Chargé · PM',
      period:         'Période de la demande de paiement · Period',
    },
    totalsRows: {
      subtotal:              'Sous-total · Subtotal',
      holdback:              'Retenue (10%) · Holdback',
      subtotalAfterHoldback: 'Sous-total après retenue · After holdback',
      tps:                   'TPS (5%)',
      tvq:                   'TVQ (9,975%)',
      netTotal:              'TOTAL NET · NET TOTAL',
    },
    termsLine1: '· Demande conforme aux travaux exécutés au {period_end}.',
    termsLine2: '· Payment request based on work completed as of {period_end}.',
    termsLine3: '· Soumis selon les modalités du contrat. · Submitted per contract terms.',
    noClient: '—',
    page: (n, t) => `Page ${n} sur ${t}`,
  },
};

// ─── Helpers ──────────────────────────────────────────────────
const formatDate = (iso, language = 'en') => {
  if (!iso) return '—';
  try {
    const locale = language === 'fr' ? 'fr-CA' : 'en-CA';
    return new Date(iso)?.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
};

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return fmtCAD(0);
  return fmtCAD(value);
};

const formatPercent = (value, contractAmount) => {
  const ca = parseFloat(contractAmount);
  const val = parseFloat(value);
  if (!ca || ca === 0 || isNaN(ca) || isNaN(val)) return '—';
  return `${(val / ca * 100)?.toFixed(1)} %`;
};

const formatPeriodDate = (dateString) => {
  if (!dateString) return '—';
  return String(dateString)?.slice(0, 10);
};

// ─── Styles ───────────────────────────────────────────────────
const styles = StyleSheet?.create({
  page: {
    fontFamily: 'Helvetica',
    backgroundColor: COLORS?.white,
    paddingHorizontal: SPACING?.pageMarginPt,
    paddingTop: 32,
    paddingBottom: 44,
  },

  // ── Company name fallback (no-logo path only) ──
  companyNameLarge: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 16,
    color: COLORS?.black,
    marginBottom: 4,
  },

  // ── Info blocks row (3-column layout) ──
  infoBlocksRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 0,
  },
  infoBlockColumn: {
    flex: 1,
  },

  // ── Period row (centered, full-width, below info columns) ──
  periodWrapper: {
    borderTopWidth: 0.5,
    borderTopStyle: 'dotted',
    borderTopColor: COLORS?.gray300,
    borderBottomWidth: 0.5,
    borderBottomStyle: 'dotted',
    borderBottomColor: COLORS?.gray300,
    paddingVertical: 6,
    marginBottom: 8,
    alignItems: 'center',
  },
  periodText: {
    fontSize: 9,
    color: COLORS?.gray500,
    textAlign: 'center',
  },
  periodValueInline: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: COLORS?.black,
  },

  // ── Section pill wrap (marginTop/marginBottom around PdfSectionTitle) ──
  sectionPillWrap: {
    marginTop: 14,
    marginBottom: 6,
  },

  // ── Table wrapper margin ──
  tableWrapper: {
    marginBottom: 14,
  },
});

// ─── Sub-components ───────────────────────────────────────────
// PillLabel delegates to PdfSectionTitle, wrapped for marginTop/marginBottom
const PillLabel = ({ label }) => (
  <View style={styles?.sectionPillWrap}>
    <PdfSectionTitle label={label} />
  </View>
);

// ─── PaymentRequestDocument ───────────────────────────────────
/**
 * @param {object} props
 * @param {object} props.request            - payment_requests row
 * @param {Array}  props.items              - enriched line items
 * @param {string} props.projectName
 * @param {string} props.projectNumber
 * @param {string} [props.clientProjectNumber] - client-side project number
 * @param {string} props.clientName         - legacy flat string (fallback)
 * @param {string} props.companyName        - contractor company name
 * @param {string} [props.logoUrl]          - base64 data URL
 * @param {string} [props.language]         - 'en' | 'fr'
 * @param {string} [props.periodStart]      - YYYY-MM-DD
 * @param {string} [props.periodEnd]        - YYYY-MM-DD
 * @param {string} [props.projectAddress]   - composed installation address
 * @param {string} [props.pmFullName]       - PM full name
 * @param {string} [props.contractorAddress]- contractor address from org
 * @param {string} [props.contractorPhone]  - contractor phone
 * @param {Array}  [props.professionals]    - [{role, company_name, contact_person, contact_phone, contact_email}]
 * @param {Array}  [props.clients]          - [{role, company_name, contact_person, address, contact_phone, contact_email}]
 * @param {string} [props.tagline]          - org tagline
 * @param {string} [props.rbqNumber]        - org RBQ number
 * @param {string} [props.tpsNumber]        - org TPS number
 * @param {string} [props.tvqNumber]        - org TVQ number
 * @param {string} [props.orgEmail]         - org email
 * @param {string} [props.orgWebsite]       - org website
 * @param {string} [props.orgAddress]       - org address (same as contractorAddress)
 * @param {string} [props.orgPhone]         - org phone (same as contractorPhone)
 * @param {number} [props.retentionRate]    - holdback rate as a whole number (e.g. 10 = 10%), defaults to 10
 */
const PaymentRequestDocument = ({
  request,
  items = [],
  projectName,
  projectNumber,
  clientProjectNumber,
  clientName,
  companyName,
  logoUrl,
  language = 'en',
  periodStart,
  periodEnd,
  projectAddress,
  pmFullName,
  contractorAddress,
  contractorPhone,
  professionals = [],
  clients = [],
  tagline,
  rbqNumber,
  tpsNumber,
  tvqNumber,
  orgEmail,
  orgWebsite,
  orgAddress,
  orgPhone,
  retentionRate = 10,
}) => {
  const t       = labels?.[language] || labels?.en;
  const company = companyName || 'Company';
  const dateStr = formatDate(request?.created_at, language);
  const dpNum   = request?.number ?? '?';

  // Resolve org address/phone from either new or legacy props
  const resolvedAddress = orgAddress || contractorAddress || null;
  const resolvedPhone   = orgPhone   || contractorPhone   || null;

  // Resolve period dates
  const resolvedPeriodStart = periodStart || request?.period_start || null;
  const resolvedPeriodEnd   = periodEnd   || request?.period_end   || null;
  const periodRangeStr = language === 'fr'
    ? `du ${formatPeriodDate(resolvedPeriodStart)} au ${formatPeriodDate(resolvedPeriodEnd)}`
    : `from ${formatPeriodDate(resolvedPeriodStart)} to ${formatPeriodDate(resolvedPeriodEnd)}`;

  // Status label map
  const statusLabels = {
    en: { draft: 'Draft', submitted: 'Submitted', approved: 'Approved' },
    fr: { draft: 'Brouillon', submitted: 'Soumis', approved: 'Approuvé' },
  };
  const statusLabel = (statusLabels?.[language] || statusLabels?.en)?.[request?.status] || (request?.status || '');

  // Split items
  const contractualItems = items?.filter(item => !item?.isExtra);
  const extrasItems      = items?.filter(item => item?.isExtra);

  // Totals
  const subtotal = items?.reduce((s, item) => s + (parseFloat(item?.monthlyAmount) || 0), 0);
  const contractTotal  = items?.reduce((s, item) => s + (parseFloat(item?.contractAmount) || 0), 0);
  const toDateTotal    = items?.reduce((s, item) => s + (parseFloat(item?.currentAmount)  || 0), 0);
  const previousTotal  = items?.reduce((s, item) => s + (parseFloat(item?.previousAmount) || 0), 0);
  const { retention: holdback, avantTaxes: subtotalAfterHoldback, tps: tpsAmt, tvq: tvqAmt, totalAvecTaxes: netTotal } = computeDpTotals(subtotal, retentionRate);

  // First client for FACTURÉ À block
  const firstClient = clients?.[0] || null;

  // Build contact line for header
  const contactParts = [resolvedPhone, orgEmail, orgWebsite]?.filter(Boolean);
  const contactLine = contactParts?.join(' · ');

  // Tax info line (left column)
  const taxParts = [
    rbqNumber ? `RBQ : ${rbqNumber}` : null,
    tpsNumber ? `TPS : ${tpsNumber}` : null,
    tvqNumber ? `TVQ : ${tvqNumber}` : null,
  ]?.filter(Boolean);
  const taxLine = taxParts?.join(' · ');

  // Terms text
  const periodEndFormatted = formatPeriodDate(resolvedPeriodEnd);
  const termsLine1 = (t?.termsLine1 || '')?.replace('{period_end}', periodEndFormatted);
  const termsLine2 = (t?.termsLine2 || '')?.replace('{period_end}', periodEndFormatted);
  const termsLine3 = t?.termsLine3 || '';

  // Bilingual labels for sections
  const clientProjNoLabel = language === 'fr' ? 'N° projet client · Client Proj. No.' : 'Client Proj. No. · N° projet client';
  const approvalSectionLabel = language === 'fr' ? 'APPROBATION · APPROVAL' : 'APPROBATION · APPROVAL';
  const signatureLabel = language === 'fr' ? 'Signature' : 'Signature';
  const dateLabel = language === 'fr' ? 'Date' : 'Date';
  const clientRoleLabel = language === 'fr' ? 'CLIENT · CLIENT' : 'CLIENT · CLIENT';

  // ── PdfHeader: meta rows ──
  const headerMeta = [
    { label: t?.dpLabel,     value: `DP${dpNum}` },
    { label: t?.dateLabel,   value: dateStr },
    ...(statusLabel ? [{ label: t?.statusLabel, value: statusLabel }] : []),
  ];

  // ── FACTURÉ À rows for PdfMetaRow ──
  const billToRows = [
    { label: t?.infoLabels?.name,    value: firstClient?.contact_person || firstClient?.company_name || clientName || '—' },
    { label: t?.infoLabels?.company,  value: firstClient?.company_name || clientName || '—' },
    { label: t?.infoLabels?.address,  value: firstClient?.address || '—' },
    { label: t?.infoLabels?.phone,    value: firstClient?.contact_phone || '—' },
    { label: t?.infoLabels?.email,    value: firstClient?.contact_email || '—' },
  ];

  // ── PROJET rows for PdfMetaRow ──
  const projectRows = [
    { label: t?.infoLabels?.projectName,   value: projectName || '—' },
    { label: t?.infoLabels?.projectNumber, value: projectNumber || '—' },
    { label: clientProjNoLabel,             value: clientProjectNumber || '—' },
    { label: t?.infoLabels?.site,          value: projectAddress || '—' },
    { label: t?.infoLabels?.pm,            value: pmFullName || '—' },
  ];

  // ── PdfApprovalSection: build cards array (professionals first, client last) ──
  const professionalCards = professionals?.map(prof => ({
    role: prof?.role || prof?.profession || '—',
    name: prof?.contact_person || prof?.company_name || '—',
    info: [prof?.company_name, prof?.contact_phone]?.filter(Boolean)?.join(' · ') || '—',
    accent: false,
  }));

  // Client card — from clients prop (first entry), fallback to companyName prop
  const clientCard = (() => {
    if (firstClient) {
      return {
        role: clientRoleLabel,
        name: firstClient?.contact_person || firstClient?.company_name || clientName || '—',
        info: [firstClient?.company_name, firstClient?.contact_phone]?.filter(Boolean)?.join(' · ') || '—',
        accent: true,
      };
    }
    // Fallback: build from companyName prop so the card is never missing
    return {
      role: clientRoleLabel,
      name: clientName || companyName || '—',
      info: companyName || '—',
      accent: true,
    };
  })();

  const approvalCards = [...professionalCards, clientCard];

  // ── PdfTotalsBlock rows ──
  const holdbackLabelEn = `Holdback (${retentionRate}%) · Retenue`;
  const holdbackLabelFr = `Retenue (${retentionRate}%) · Holdback`;
  const holdbackLabel = language === 'fr' ? holdbackLabelFr : holdbackLabelEn;

  const totalsRows = [
    { label: t?.totalsRows?.subtotal,              value: formatCurrency(subtotal) },
    { label: holdbackLabel,                        value: formatCurrency(holdback) },
    { label: t?.totalsRows?.subtotalAfterHoldback, value: formatCurrency(subtotalAfterHoldback) },
    { label: t?.totalsRows?.tps,                   value: formatCurrency(tpsAmt) },
    { label: t?.totalsRows?.tvq,                   value: formatCurrency(tvqAmt) },
    { isDivider: true },
    { label: t?.totalsRows?.netTotal,              value: formatCurrency(netTotal), highlight: true },
  ];

  // ── PdfItemsTable: grouped-header definition (DP 2-row header) ──
  // groups defines the parent row; rowspan=true cells span both rows (blank sub-row spacer).
  // subCols defines the sub-header cells under each spanning group.
  const tableGroups = [
    { label: t?.columns?.description,  flex: 3,   rowspan: true },
    { label: t?.columns?.contractValue, flex: 1.5, rowspan: true },
    { label: t?.columns?.toDateParent,  flex: 2.2, subCols: [{ label: t?.columns?.pct, flex: 1 }, { label: t?.columns?.dollar, flex: 1 }] },
    { label: t?.columns?.lastReqParent, flex: 2.2, subCols: [{ label: t?.columns?.pct, flex: 1 }, { label: t?.columns?.dollar, flex: 1 }] },
    { label: t?.columns?.thisRequest,  flex: 1.5, rowspan: true },
  ];

  // ── Total row for the contractual work table ──
  // columns order: description | contractAmount | toDatePct | toDateAmt | lastReqPct | lastReqAmt | monthlyAmount
  // cells[0] maps to contractAmount column (index 1), so cells has 6 entries (indices 0–5 for cols 1–6)
  const contractualTotalRow = {
    label: 'TOTAL',
    cells: [
      formatCurrency(contractTotal),  // contractAmount col
      null,                           // toDatePct col — do not total percentages
      formatCurrency(toDateTotal),    // toDateAmt col
      null,                           // lastReqPct col — do not total percentages
      formatCurrency(previousTotal),  // lastReqAmt col
      formatCurrency(subtotal),       // monthlyAmount col
    ],
  };

  // Helper to build columns with isExtras flag baked in via closure
  const buildTableColumns = (isExtrasTable) => [
    {
      key: 'description',
      flex: 3,
      align: 'left',
      renderCell: (item) => (
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {isExtrasTable && item?.extraCode ? (
            <Text style={{ fontSize: 8, color: COLORS?.black, fontFamily: 'Helvetica-Bold', marginRight: 3 }}>{item?.extraCode} — </Text>
          ) : null}
          <Text style={{ fontSize: 8.5, color: COLORS?.black }}>{item?.description || '—'}</Text>
        </View>
      ),
    },
    { key: 'contractAmount', flex: 1.5, align: 'right', renderCell: (item) => formatCurrency(parseFloat(item?.contractAmount) || 0) },
    { key: 'toDatePct',      flex: 1,   align: 'right', renderCell: (item) => formatPercent(item?.currentAmount, item?.contractAmount) },
    { key: 'toDateAmt',      flex: 1,   align: 'right', renderCell: (item) => formatCurrency(parseFloat(item?.currentAmount) || 0) },
    { key: 'lastReqPct',     flex: 1,   align: 'right', renderCell: (item) => formatPercent(item?.previousAmount, item?.contractAmount) },
    { key: 'lastReqAmt',     flex: 1,   align: 'right', renderCell: (item) => formatCurrency(parseFloat(item?.previousAmount) || 0) },
    { key: 'monthlyAmount',  flex: 1.5, align: 'right', renderCell: (item) => formatCurrency(parseFloat(item?.monthlyAmount) || 0) },
  ];

  return (
    <Document>
      <Page size="A4" style={styles?.page}>

        {/* ── STEP 4: Header → PdfHeader ── */}
        <PdfHeader
          logoBase64={logoUrl || null}
          docTitle={t?.title}
          docSubtitle={t?.titleSub}
          meta={headerMeta}
          tagline={tagline || ''}
          address={resolvedAddress || ''}
          contactLine={contactLine || ''}
          taxLine={taxLine || ''}
        />

        {/* ── Two info columns: FACTURÉ À (left) + PROJET (right) ──
            PROFESSIONNELS column removed per DP layout update (Change 1). ── */}
        <View style={styles?.infoBlocksRow}>
          {/* COL 1: FACTURÉ À */}
          <View style={styles?.infoBlockColumn}>
            <PdfSectionTitle label={t?.billTo} />
            {billToRows?.map((row, idx) => (
              <PdfMetaRow
                key={idx}
                label={row?.label}
                value={row?.value}
                hasBorder={idx < billToRows?.length - 1}
              />
            ))}
          </View>

          {/* COL 2: PROJET */}
          <View style={styles?.infoBlockColumn}>
            <PdfSectionTitle label={t?.project} />
            {projectRows?.map((row, idx) => (
              <PdfMetaRow
                key={idx}
                label={row?.label}
                value={row?.value}
                hasBorder={idx < projectRows?.length - 1}
              />
            ))}
          </View>
        </View>

        {/* ── Période: centered full-width row, dotted top + bottom rules (Change 2) ── */}
        <View style={styles?.periodWrapper}>
          <Text style={styles?.periodText}>
            {t?.infoLabels?.period}{' '}
            <Text style={styles?.periodValueInline}>{periodRangeStr}</Text>
          </Text>
        </View>

        {/* ── STEP 8: Contractual Lines → PdfItemsTable with groups ── */}
        {contractualItems?.length > 0 ? (
          <>
            <PillLabel label={t?.contractualBanner} />
            <View style={styles?.tableWrapper}>
              <PdfItemsTable
                items={contractualItems}
                columns={buildTableColumns(false)}
                groups={tableGroups}
                isExtras={false}
                totalRow={contractualTotalRow}
              />
            </View>
          </>
        ) : null}

        {/* ── Extras ── */}
        {extrasItems?.length > 0 ? (
          <>
            <PillLabel label={t?.extrasBanner} />
            <View style={styles?.tableWrapper}>
              <PdfItemsTable
                items={extrasItems}
                columns={buildTableColumns(true)}
                groups={tableGroups}
                isExtras={true}
                totalRow={null}
              />
            </View>
          </>
        ) : null}

        {/* ── STEP 9: Totals block → PdfTotalsBlock ── */}
        <PdfTotalsBlock
          termsLines={[termsLine1, termsLine2, termsLine3]}
          rows={totalsRows}
        />

        {/* ── Approval section → PdfApprovalSection (cards API, Change 3) ── */}
        <PdfApprovalSection
          title={approvalSectionLabel}
          cards={approvalCards}
          signatureLabel={signatureLabel}
          dateLabel={dateLabel}
        />

        {/* ── STEP 2: Footer → PdfFooter with renderRight for dynamic page numbers ── */}
        <PdfFooter
          leftText={`By OnsitePM`}
          renderRight={({ pageNumber, totalPages }) => t?.page(pageNumber, totalPages)}
        />

      </Page>
    </Document>
  );
};

export default PaymentRequestDocument;
